import { GuestData, Order } from '../models';

type MemberRef = string | { _id?: string; id?: string };

const normalizeId = (value: MemberRef | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  return value._id || value.id || null;
};

export interface GuestMemberSyncDecision {
  guestMemberId: string | null;
  currentMemberId: string | null;
  shouldAssign: boolean;
  requiresConfirmation: boolean;
}

export const getGuestMemberId = (guest?: GuestData | null): string | null => {
  if (!guest) {
    return null;
  }
  return normalizeId((guest.memberId as MemberRef) || (guest.member as MemberRef));
};

const MONGO_OBJECT_ID_LIKE = /^[a-f\d]{24}$/i;

interface PopulatedMemberStub {
  memberId?: string;
  _id?: string;
  id?: string;
}

/**
 * Human-visible member number (e.g. club "PARTAKE"), not Mongo `_id`, when the API populated `member` or `memberId`.
 */
export const getGuestMemberDisplayLabel = (guest?: GuestData | null): string => {
  if (!guest) {
    return 'No member ID';
  }
  const fromMember = guest.member as PopulatedMemberStub | undefined;
  if (fromMember?.memberId) {
    return fromMember.memberId;
  }
  const mid = guest.memberId as unknown;
  if (mid && typeof mid === 'object' && !Array.isArray(mid)) {
    const populated = mid as PopulatedMemberStub;
    if (populated.memberId) {
      return populated.memberId;
    }
  }
  if (typeof mid === 'string' && mid) {
    if (MONGO_OBJECT_ID_LIKE.test(mid)) {
      return 'Linked member';
    }
    return mid;
  }
  return 'No member ID';
};

export const getOrderMemberId = (order: Order): string | null => {
  return normalizeId(order?.data?.member as MemberRef);
};

export const getGuestMemberSyncDecision = (
  order: Order,
  guest?: GuestData | null
): GuestMemberSyncDecision => {
  const guestMemberId = getGuestMemberId(guest);
  const currentMemberId = getOrderMemberId(order);
  if (!guestMemberId) {
    return {
      guestMemberId,
      currentMemberId,
      shouldAssign: false,
      requiresConfirmation: false,
    };
  }
  if (!currentMemberId || currentMemberId === guestMemberId) {
    return {
      guestMemberId,
      currentMemberId,
      shouldAssign: true,
      requiresConfirmation: false,
    };
  }
  return {
    guestMemberId,
    currentMemberId,
    shouldAssign: true,
    requiresConfirmation: true,
  };
};
